/**
 * Media validation config for the admin app.
 *
 * Mirrors `yoga_school_admin_dashboard/src/config/mediaValidation.ts` so that
 * the admin-driven course create flow applies the exact same client-side
 * limits/MIME types as the school dashboard. Keep them in sync.
 */

export const FILE_SIZE_LIMITS = {
  IMAGE: 2 * 1024 * 1024, // 2MB
  VIDEO: 20 * 1024 * 1024, // 20MB
  DOCUMENT: 2 * 1024 * 1024, // 2MB
} as const

export const ACCEPTED_MIME_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  VIDEO: ['video/mp4', 'video/webm'],
  DOCUMENT: ['application/pdf', 'application/x-pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
} as const

export const ACCEPT_STRINGS = {
  IMAGE: 'image/jpeg,image/jpg,image/png,image/webp',
  VIDEO: 'video/mp4,video/webm',
  DOCUMENT: 'application/pdf,application/x-pdf,image/jpeg,image/jpg,image/png,image/webp',
} as const

export type MediaSection =
  | 'courseMedia'
  | 'courseDescription'
  | 'schoolLogo'
  | 'schoolCoverImage'
  | 'schoolGallery'
  | 'schoolIntroVideo'
  | 'schoolVideoGallery'
  | 'roomImages'
  | 'roomVideo'
  | 'verificationDocument'
  | 'verificationDocumentAdditional'

export interface MediaSectionConfig {
  maxImages?: number
  maxVideos?: number
  maxDocuments?: number
  maxImageSize?: number
  maxVideoSize?: number
  maxDocumentSize?: number
  acceptImages?: string
  acceptVideos?: string
  acceptDocuments?: string
  allowMultipleImages?: boolean
  allowMultipleVideos?: boolean
}

export const MEDIA_SECTIONS: Record<MediaSection, MediaSectionConfig> = {
  courseMedia: {
    maxImages: 5,
    maxVideos: 2,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
    allowMultipleImages: true,
    allowMultipleVideos: false,
  },
  courseDescription: {
    maxImages: 2,
    maxVideos: 1,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
  },
  schoolLogo: { maxImages: 1, maxImageSize: FILE_SIZE_LIMITS.IMAGE, acceptImages: ACCEPT_STRINGS.IMAGE },
  schoolCoverImage: { maxImages: 1, maxImageSize: FILE_SIZE_LIMITS.IMAGE, acceptImages: ACCEPT_STRINGS.IMAGE },
  schoolGallery: {
    maxImages: 10,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    allowMultipleImages: true,
  },
  schoolIntroVideo: { maxVideos: 1, maxVideoSize: FILE_SIZE_LIMITS.VIDEO, acceptVideos: ACCEPT_STRINGS.VIDEO },
  schoolVideoGallery: {
    maxVideos: 4,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
    allowMultipleVideos: true,
  },
  roomImages: {
    maxImages: 4,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    allowMultipleImages: true,
  },
  roomVideo: { maxVideos: 1, maxVideoSize: FILE_SIZE_LIMITS.VIDEO, acceptVideos: ACCEPT_STRINGS.VIDEO },
  verificationDocument: {
    maxDocuments: 10,
    maxDocumentSize: FILE_SIZE_LIMITS.DOCUMENT,
    acceptDocuments: ACCEPT_STRINGS.DOCUMENT,
  },
  verificationDocumentAdditional: {
    maxDocuments: 5,
    maxDocumentSize: FILE_SIZE_LIMITS.DOCUMENT,
    acceptDocuments: ACCEPT_STRINGS.DOCUMENT,
  },
}

export interface ValidationResult {
  valid: boolean
  error?: string
}

export function validateImageFile(file: File, section: MediaSection, currentCount = 0): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxImages = config.maxImages ?? 0
  const maxSize = config.maxImageSize ?? FILE_SIZE_LIMITS.IMAGE

  if (!(ACCEPTED_MIME_TYPES.IMAGE as readonly string[]).includes(file.type)) {
    return { valid: false, error: 'Invalid image type. Allowed: JPEG, PNG, WebP.' }
  }
  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return { valid: false, error: `Image must be under ${maxMB}MB.` }
  }
  if (maxImages > 0 && currentCount >= maxImages) {
    return { valid: false, error: `Maximum ${maxImages} image(s) allowed for this section.` }
  }
  return { valid: true }
}

export function validateVideoFile(file: File, section: MediaSection, currentCount = 0): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxVideos = config.maxVideos ?? 0
  const maxSize = config.maxVideoSize ?? FILE_SIZE_LIMITS.VIDEO

  if (!(ACCEPTED_MIME_TYPES.VIDEO as readonly string[]).includes(file.type)) {
    return { valid: false, error: 'Invalid video type. Allowed: MP4, WebM.' }
  }
  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return { valid: false, error: `Video must be under ${maxMB}MB.` }
  }
  if (maxVideos > 0 && currentCount >= maxVideos) {
    return { valid: false, error: `Maximum ${maxVideos} video(s) allowed for this section.` }
  }
  return { valid: true }
}

export function validateImageFiles(files: File[], section: MediaSection, currentCount = 0): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxImages = config.maxImages ?? 0
  const allowedCount = maxImages > 0 ? maxImages - currentCount : files.length
  if (files.length > allowedCount) {
    return {
      valid: false,
      error: `You can add at most ${allowedCount} more image(s). Maximum ${maxImages} for this section.`,
    }
  }
  for (let i = 0; i < files.length; i++) {
    const result = validateImageFile(files[i], section, currentCount + i)
    if (!result.valid) return result
  }
  return { valid: true }
}

export function validateVideoFiles(files: File[], section: MediaSection, currentCount = 0): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxVideos = config.maxVideos ?? 0
  const allowedCount = maxVideos > 0 ? maxVideos - currentCount : files.length
  if (files.length > allowedCount) {
    return {
      valid: false,
      error: `You can add at most ${allowedCount} more video(s). Maximum ${maxVideos} for this section.`,
    }
  }
  for (let i = 0; i < files.length; i++) {
    const result = validateVideoFile(files[i], section, currentCount + i)
    if (!result.valid) return result
  }
  return { valid: true }
}

/** Validate a document file (PDF or image) — mirrors yoga school admin dashboard */
export function validateDocumentFile(
  file: File,
  section: MediaSection,
  currentCount = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxDocs = config.maxDocuments ?? 10
  const maxSize = config.maxDocumentSize ?? FILE_SIZE_LIMITS.DOCUMENT
  const accepted = ACCEPTED_MIME_TYPES.DOCUMENT as readonly string[]

  const mimeOk =
    accepted.includes(file.type) ||
    (file.type === '' && /\.(pdf|jpe?g|png|webp)$/i.test(file.name))
  if (!mimeOk) {
    return { valid: false, error: 'Invalid file type. Please upload PDF or images (JPEG, PNG, WebP).' }
  }
  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return { valid: false, error: `Document must be under ${maxMB}MB.` }
  }
  if (maxDocs > 0 && currentCount >= maxDocs) {
    return { valid: false, error: `Maximum ${maxDocs} document(s) allowed for this section.` }
  }
  return { valid: true }
}
